import { useTranslation } from 'react-i18next';
import type { ConfigCustomFieldDto, InformationFieldParams } from 'korint-sdk';
import type { CountryCode } from 'libphonenumber-js/max';
import { Separator } from '../../ui/separator';
import type { FieldType, InformationCardProps } from './InformationCard.type';
import { DynamicInformationFields } from './DynamicInformationFields';
import { InformationField } from './InformationField';
import { InformationArrayField } from './InformationArrayField';
import { useCustomFieldConfigs } from './useCustomFieldConfigs';
import { Text } from '@folio/components/ui/text';

export const getEditionMode = (
  field: ConfigCustomFieldDto | undefined,
  { allowEdition = true, forceReadonly }: { allowEdition?: boolean; forceReadonly?: boolean },
) => {
  if (!field) {
    return 'DISABLED' as const;
  }

  if (!allowEdition || forceReadonly) {
    return 'DISABLED' as const;
  }
  if (field.isCalculated) {
    return 'DISABLED' as const;
  }

  if (field.mtaModification === 'FORBIDDEN') {
    return 'DISABLED' as const;
  }

  if (field.requiresOnChange === 'QUOTE') {
    return 'FORM' as const;
  }
  if (field.requiresOnChange === 'CONFIRMATION') {
    return 'FORM' as const;
  }
  if (field.requiresOnChange === 'NOTHING') {
    return 'INLINE' as const;
  }

  return 'DISABLED' as const;
};

const customFieldTypeMap: Partial<Record<ConfigCustomFieldDto['type'], FieldType>> = {
  BOOLEAN: 'boolean',
  NUMBER: 'number',
  INTEGER: 'number',
  DATE: 'date',
  DATETIME: 'date',
  ENUM: 'select',
  PHONE_NUMBER: 'tel',
  POSTCODE: 'postcode',
  EMAIL: 'email',
  SIRET: 'siret',
  ARRAY: 'array',
};

export const resolveFieldShortcutFromConfig = (
  shortcut: string | InformationFieldParams,
  customFieldConfigs: ConfigCustomFieldDto[] = [],
): (Omit<InformationFieldParams, 'type'> & { type: FieldType }) | null => {
  const key = typeof shortcut === 'string' ? shortcut : shortcut.key;

  const customFieldConfig = customFieldConfigs.find(field => field.key === key);

  if (!customFieldConfig) {
    return null;
  }

  const inferredType = customFieldTypeMap[customFieldConfig.type] ?? 'text';

  const countriesRuleValue =
    inferredType === 'postcode' || inferredType === 'tel'
      ? customFieldConfig.validationRules?.find(({ option }) => option === 'countries')?.value
      : undefined;

  const inferredCountries = Array.isArray(countriesRuleValue)
    ? (countriesRuleValue as CountryCode[])
    : undefined;

  return {
    key,
    type: inferredType,
    ...(inferredCountries ? { countries: inferredCountries } : {}),
    ...(typeof shortcut === 'string' ? {} : shortcut),
  };
};

type InformationFieldsProps = Pick<
  InformationCardProps,
  'productId' | 'fieldsConfig' | 'entityType' | 'entity' | 'updateEntity'
> & {
  redirectToForm?: InformationCardProps['redirectToForm'];
  allowEdition?: boolean;
  forceReadonly?: boolean;
};

export const InformationFields = ({
  productId,
  fieldsConfig,
  entityType,
  entity,
  updateEntity,
  redirectToForm,
  allowEdition = true,
  forceReadonly,
}: InformationFieldsProps) => {
  const { t } = useTranslation();
  const customFieldConfigs = useCustomFieldConfigs({ productId, entityType });

  return (
    <table className="w-full table-fixed">
      <tbody>
        {fieldsConfig.map((keyOrParams, index) => {
          if (typeof keyOrParams === 'object' && keyOrParams.type === 'separator') {
            if (
              keyOrParams.hideIfFieldEmpty &&
              entity.customFields[keyOrParams.hideIfFieldEmpty] == null
            ) {
              return null;
            }
            return (
              <tr key={`separator-${index}`}>
                <td colSpan={2} className="pb-1 pt-3">
                  {keyOrParams.id && (
                    <Text
                      size="xs"
                      weight="semibold"
                      tone="muted"
                      className="mb-1 uppercase tracking-wide"
                    >
                      {t([
                        `${productId}.separators.${keyOrParams.id}`,
                        `separators.${keyOrParams.id}`,
                      ])}
                    </Text>
                  )}
                  <Separator />
                </td>
              </tr>
            );
          }

          const key = typeof keyOrParams === 'string' ? keyOrParams : keyOrParams.key;

          const fieldParams = resolveFieldShortcutFromConfig(
            keyOrParams as string | InformationFieldParams,
            customFieldConfigs,
          );
          if (!fieldParams) {
            return null;
          }

          const value = entity.customFields[key] as unknown as
            | string
            | number
            | boolean
            | undefined;

          const display = fieldParams.hideIfEmpty ? value !== undefined : true;

          if (!display) {
            return null;
          }

          const customFieldConfig = customFieldConfigs.find(field => field.key === key);

          const editionMode = getEditionMode(customFieldConfig, {
            allowEdition,
            forceReadonly: fieldParams.forceReadonly || forceReadonly,
          });

          if (fieldParams.type === 'array') {
            return (
              customFieldConfig && (
                <InformationArrayField
                  key={key}
                  fieldKey={key}
                  productId={productId}
                  fieldConfig={customFieldConfig}
                  value={entity.customFields[key]}
                  editionMode={editionMode}
                  redirectToForm={redirectToForm}
                  updateEntity={updateEntity}
                />
              )
            );
          }

          const options =
            fieldParams.type === 'select'
              ? (customFieldConfig?.validationRules?.find(({ option }) => option === 'enum')
                  ?.value as string[] | undefined)
              : undefined;

          return (
            <InformationField
              key={key}
              fieldKey={key}
              productId={productId}
              redirectToForm={redirectToForm}
              updateEntity={updateEntity}
              editionMode={editionMode}
              type={fieldParams.type}
              options={options}
              value={value}
              formatValue={fieldParams.formatValue}
              validationRules={
                fieldParams.countries ? { countries: fieldParams.countries } : undefined
              }
            />
          );
        })}
        <DynamicInformationFields
          productId={productId}
          fieldsConfig={fieldsConfig}
          entity={entity}
          customFieldConfigs={customFieldConfigs}
          updateEntity={updateEntity}
          redirectToForm={redirectToForm}
          allowEdition={allowEdition}
          forceReadonly={forceReadonly}
        />
      </tbody>
    </table>
  );
};
